import { Injectable, inject } from '@angular/core';
import {
  ConfigurableFocusTrap,
  ConfigurableFocusTrapFactory,
} from '@angular/cdk/a11y';

/**
 * Le focus d'un dialogue : maintenu tant qu'il est ouvert, rendu à son
 * déclencheur à la fermeture.
 *
 * ## Pourquoi c'est nécessaire
 *
 * Un volet ou une fenêtre qui s'ouvre sans piéger le focus laisse
 * l'utilisateur continuer à tabuler dans la page DERRIÈRE, qu'il ne voit plus.
 * Il remplit des champs invisibles, actionne des boutons masqués, et n'a aucun
 * moyen de savoir où il se trouve. Et à la fermeture, sans rendu du focus,
 * celui-ci retombe sur `<body>` : la tabulation suivante repart du tout début
 * de la page, pas du bouton qui avait ouvert le dialogue.
 *
 * ## Pourquoi un service partagé plutôt qu'une directive
 *
 * Les deux dialogues du produit s'ouvrent par des chemins différents — le volet
 * par une entrée `isVisible`, la fenêtre par une méthode `open()` appelée
 * depuis un service. Une directive devrait deviner l'ouverture depuis le DOM ;
 * un service est appelé à l'instant exact où chacun sait qu'il s'ouvre.
 *
 * ## Une pile, et pas une seule référence
 *
 * Une fenêtre peut s'ouvrir par-dessus un volet. Mémoriser un seul déclencheur
 * ferait perdre le premier : à la fermeture de la fenêtre, le focus reviendrait
 * dans la page plutôt que dans le volet resté ouvert.
 */
@Injectable({ providedIn: 'root' })
export class DialogFocusService {
  private readonly trapFactory = inject(ConfigurableFocusTrapFactory);

  /** Un niveau par dialogue ouvert, dépilé à la fermeture. */
  private readonly pile: Array<{
    element: HTMLElement;
    trap: ConfigurableFocusTrap;
    declencheur: HTMLElement | null;
  }> = [];

  /**
   * Piège le focus dans `element` et mémorise d'où il venait.
   *
   * Sans effet si l'élément est déjà piégé : les composants appellent parfois
   * leur propre ouverture deux fois (un `@Input` réaffecté à la même valeur),
   * et un second piège ferait perdre le déclencheur d'origine.
   */
  capture(element: HTMLElement): void {
    if (this.pile.some((n) => n.element === element)) {
      return;
    }

    const declencheur =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const trap = this.trapFactory.create(element);
    this.pile.push({ element, trap, declencheur });

    // Focus pris TOUT DE SUITE, et non par `focusInitialElementWhenReady` :
    // cette variante attend la stabilité de la zone Angular et garde donc un
    // abonnement ouvert. Un banc qui déclenche son premier cycle de rendu sur
    // cette même stabilité ne le déclenche alors jamais — le dialogue reste
    // fermé à l'écran alors que son modèle le dit ouvert.
    //
    // En contrepartie, l'appelant doit appeler `capture` quand le contenu du
    // dialogue est RENDU, pas quand il décide de s'ouvrir. Les deux appelants
    // le font : la fenêtre après avoir démasqué son élément, le volet depuis
    // `afterNextRender`.
    trap.focusInitialElement();
  }

  /** Libère le piège et rend le focus au déclencheur mémorisé. */
  release(element: HTMLElement): void {
    // DERNIERE occurrence : c'est une pile. Prendre la premiere rendrait le
    // focus du mauvais niveau si un meme element etait capture deux fois, et
    // masquerait la disparition de la garde ci-dessus.
    //
    // Boucle plutot que `findLastIndex` : celui-ci exige la bibliotheque
    // ES2023, que le projet ne cible pas. Jest l'acceptait, `tsc` non.
    let index = -1;
    for (let i = this.pile.length - 1; i >= 0; i -= 1) {
      if (this.pile[i].element === element) {
        index = i;
        break;
      }
    }

    if (index === -1) {
      return;
    }

    const [niveau] = this.pile.splice(index, 1);
    niveau.trap.destroy();

    // `isConnected` : le déclencheur peut avoir disparu pendant que le dialogue
    // était ouvert — une ligne supprimée, une liste rechargée.
    //
    // Cette garde est une INTENTION, pas une protection : donner le focus à un
    // élément détaché ne lève pas et ne fait rien, ni en navigateur ni en test.
    // Elle n'est donc couverte par aucun cas — un test qui ne peut pas échouer
    // serait de la décoration.
    if (niveau.declencheur?.isConnected) {
      niveau.declencheur.focus();
    }
  }
}
